import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import { Phone as PhoneIcon, ContentCopy as CopyIcon, Call as CallIcon, Check as CheckIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { usePhoneSource, PHONE_E164, displayTextToE164Clipboard } from '../../context/PhoneSourceContext';

/**
 * Copies text to clipboard with fallback for older browsers and non-HTTPS.
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} - True if successful
 */
const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to execCommand
    }
  }
  return new Promise((resolve) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:none;outline:none;overflow:hidden;clip:rect(0,0,0,0);';
    document.body.appendChild(textArea);
    textArea.select();
    textArea.setSelectionRange(0, text.length);
    const copyHandler = (e) => {
      e.clipboardData.setData('text/plain', text);
      e.preventDefault();
    };
    document.addEventListener('copy', copyHandler);
    const success = document.execCommand('copy');
    document.removeEventListener('copy', copyHandler);
    document.body.removeChild(textArea);
    resolve(success);
  });
};

/**
 * Call Now — reads tel/display from global #phone-number via PhoneSourceContext.
 * Mobile: tel link; desktop: dialog. Button label stays "Call Now" only.
 */
const CallNowButton = ({ sx = {}, size = 'large', variant = 'outlined', onClick, ...buttonProps }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { telHref, resolvedDisplay, syncFromDom } = usePhoneSource();
  const location = useLocation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Extra guard: re-read global source when this instance’s route segment changes (context already syncs; cheap no-op if unchanged)
  useEffect(() => {
    syncFromDom();
  }, [location.pathname, syncFromDom]);

  useEffect(() => {
    if (dialogOpen) {
      syncFromDom();
    }
  }, [dialogOpen, syncFromDom]);

  const handleClick = () => {
    onClick?.();
    if (!isMobile) {
      setDialogOpen(true);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setCopied(false);
  };

  const handleCopyNumber = async () => {
    const toCopy = displayTextToE164Clipboard(resolvedDisplay) || PHONE_E164;
    const success = await copyToClipboard(toCopy);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <>
      <Button
        component={isMobile ? 'a' : 'button'}
        href={isMobile ? telHref : undefined}
        variant={variant}
        size={size}
        startIcon={<PhoneIcon />}
        onClick={handleClick}
        aria-label="Call now"
        sx={sx}
        {...buttonProps}
      >
        Call Now
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        onKeyDown={handleKeyDown}
        aria-labelledby="call-us-dialog-title"
        aria-describedby="call-us-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            width: 380,
            minWidth: 380,
            maxWidth: 380,
            overflow: 'hidden',
            boxShadow: theme.shadows[24]
          }
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || theme.palette.info.main} 100%)`,
            color: 'white',
            py: 3,
            px: 3,
            textAlign: 'center'
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: alpha('#fff', 0.2),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}
          >
            <PhoneIcon sx={{ fontSize: 28 }} />
          </Box>
          <DialogTitle
            id="call-us-dialog-title"
            sx={{
              fontWeight: 700,
              fontSize: '1.25rem',
              color: 'white',
              p: 0,
              mb: 0.5
            }}
          >
            Call H-Auto Repair
          </DialogTitle>
          <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
            Call for a free quote
          </Typography>
        </Box>
        <DialogContent id="call-us-dialog-description" sx={{ pt: 3, pb: 2, px: 3 }}>
          <Typography
            variant="h4"
            component="p"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '1.75rem' },
              color: 'text.primary',
              textAlign: 'center',
              letterSpacing: '0.05em'
            }}
          >
            {resolvedDisplay}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 1.5,
              textAlign: 'center',
              fontSize: '0.8125rem'
            }}
          >
            Copy the number or click Open Dialer to call from your device.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: 'column',
            gap: 1.5,
            px: 3,
            pb: 3,
            pt: 0
          }}
        >
          <Button
            variant="contained"
            fullWidth
            startIcon={copied ? <CheckIcon /> : <CopyIcon />}
            onClick={handleCopyNumber}
            aria-label="Copy phone number"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              borderRadius: 2,
              ...(copied && {
                backgroundColor: theme.palette.success.main,
                '&:hover': {
                  backgroundColor: theme.palette.success.dark
                }
              })
            }}
          >
            Copy Number
          </Button>
          <Button
            component="a"
            href={telHref}
            variant="outlined"
            fullWidth
            startIcon={<CallIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              borderRadius: 2
            }}
          >
            Open Dialer
          </Button>
          <Button
            variant="text"
            fullWidth
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              fontWeight: 500
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CallNowButton;
export { PHONE_E164, PHONE_DISPLAY } from '../../context/PhoneSourceContext';
